class ProductGroupSelect extends HTMLElement {
  abortController;

  /**
   * Connected callback.
   */
  connectedCallback() {
    this.initOptions();
  }

  /**
   * Disconnected callback.
   */
  disconnectedCallback() {
    this.abortController = null;
  }

  /**
   * Initialize the options.
   */
  initOptions() {
    this.sectionId = this.dataset.sectionId;
    this.currentProductUrl = this.dataset.currentProductUrl;

    // Selectors
    this.productForm = this.closest('product-form');
    this.options = this.productForm.querySelectorAll('[data-variant-id]');

    // On click on grouped product
    this.clickHandler = (event) => {
      const target = event.currentTarget;
      if (target && !this.abortController) {
        event.preventDefault();
        this.onGroupChange({ target });
      }
    };
    this.querySelectorAll('[data-group-product-url]').forEach((option) => {
      option.addEventListener('click', this.clickHandler);
    });

    // On keydown on grouped product
    this.keydownHandler = (event) => {
      if (event.key !== 'Enter') return;
      const target = event.target.closest('[data-group-product-url]');
      if (target && !this.abortController) {
        event.preventDefault();
        this.onGroupChange({ target });
      }
    };
    this.addEventListener('keydown', this.keydownHandler);
  }

  /**
   * Get the new variant ID
   * @param {HTMLElement} target - The target element.
   * @returns {String} The variant ID.
   */
  getVariantId(target) {
    // Get the all new variants
    const newVariantsJson = JSON.parse(target.dataset.groupProductVariants);

    // Get the selected option values
    const currentSelectedOptionValues = [...this.options]
      .filter((option) => option.checked)
      .map((option) => ({
        option: option.name,
        value: option.value,
      }));

    // Get the selected variant
    let newSelectedVariant = newVariantsJson.find((variant) =>
      currentSelectedOptionValues.every((option) => variant.options.includes(option.value))
    );

    // If no variant is found, use the first variant
    if (!newSelectedVariant) {
      newSelectedVariant = newVariantsJson[0];
    }

    return newSelectedVariant.id;
  }

  /**
   * Handle on group product change.
   * @param {Event} event - The event object.
   */
  async onGroupChange({ target }) {
    // Return if this target is already selected
    if (target.classList.contains('group-option-selected')) return;

    // Create a new abort controller
    if (this.abortController) return;
    this.abortController = new AbortController();

    // Show the loader
    document.querySelector('[data-product-options-loader]').classList.add('active');

    // Get the new variant ID
    const variantId = this.getVariantId(target);

    // Immediately uncheck all other checkboxes within the same fieldset to improve perceived speed.
    this.updateFieldset(target);

    // Fetch the new product
    try {
      const params = this.createParams(variantId);
      const fetchUrl = `${target.dataset.groupProductUrl}?${params}`;

      const response = await fetch(fetchUrl, {
        signal: this.abortController.signal,
      });

      const text = await response.text();
      const parsedHTML = new DOMParser().parseFromString(text, 'text/html');

      // Update the URL
      this.updateURL(target.dataset.groupProductUrl, params);

      // Publish the new product
      publish(`product:changed:${this.sectionId}`, {
        variantId,
        html: parsedHTML,
      });
    } catch (error) {
      if (error.name === 'AbortError')
        return console.error('Variant change request was aborted due to a new variant selection.');

      console.error(error.message);
      window.toast.error(window.toast_translations.product.variant_error);
    } finally {
      this.abortController = null;
      document.querySelector(`#${target.id}`).focus();
      document.querySelector('[data-product-options-loader]').classList.remove('active');
    }
  }

  /**
   * Update URL
   * @param {String} url - URL value
   * @param {String} params - The params as a string
   */
  updateURL(url, params) {
    // Return if no URL or params, or if the URL is the same as the current product URL
    if (!url) return;
    if (!params) return;
    if (url === this.currentProductUrl) return;

    // Remove section_id from params
    const newParams = new URLSearchParams(params);
    newParams.delete('section_id');

    // Replace the current history state with the new search params, without reloading the page
    try {
      history.replaceState(null, '', `${url}?${newParams}`);
    } catch (error) {
      console.error('Error updating URL', error);
    }
  }

  /**
   * Create the params.
   * @param {String} variantId - The variant ID.
   * @returns {String} The params.
   */
  createParams(variantId) {
    const params = new URLSearchParams(window.location.search);
    params.set('section_id', this.sectionId);
    params.set('variant', variantId);
    return params.toString();
  }

  /**
   * Update the fieldset.
   * @param {HTMLElement} target - The target element.
   */
  updateFieldset(target) {
    if (!target) return;
    this.querySelectorAll('[data-group-product-url]').forEach((option) => {
      option.classList.toggle('group-option-selected', option === target);
    });
  }
}

customElements.define('product-group-select', ProductGroupSelect);
